import { Route } from '@/Enums/Routes/Route.enum';
import LogOutIcon from '@/components/Icons/LogOutIcon';
import SettingsRouteIcon from '@/components/Icons/SettingsRouteIcon';
import SideMenuGroupExpandIcon from '@/components/Icons/SideMenuGroupExpandIcon';
import { HIDDEN_SIDEBAR_WIDTH, SIDEBAR_WIDTH } from '@/constants/layout';
import { logoutCleanup } from '@/helpers/auth';
import { selectGroupedScreens, selectIsSuperAdmin } from '@/store/selectors/me.selectors';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Drawer,
  accordionDetailsClasses,
  accordionSummaryClasses,
  drawerClasses,
  styled,
  useThemeProps,
} from '@mui/material';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ScreenLinks from './ScreenLinks';
import SidebarLink from './SidebarLink';

const RouteGroup = styled(Accordion, {
  name: 'Sidebar',
  slot: 'routeGroup',
})({
  fontSize: 12,
  fontWeight: '500',
  background: 'transparent',
  boxShadow: 'none',
  padding: '0 12px',
  [`& .${accordionSummaryClasses.root}`]: {
    width: '216px',
    padding: 0,
    textTransform: 'uppercase',
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    color: 'currentColor',
  },
  [`& .${accordionDetailsClasses.root}`]: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'center',
  },
  '::before': {
    content: 'none',
  },
});

const sidebarOpenedMixin = theme => ({
  width: SIDEBAR_WIDTH,
  background: 'inherit',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  justifyContent: 'space-between',
  borderRadius: 0,
});

const sidebarClosedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: HIDDEN_SIDEBAR_WIDTH,
  background: 'inherit',
  justifyContent: 'space-between',
  borderRadius: 0,
});

const opacityOpenMixin = theme => ({
  transition: theme.transitions.create(['opacity'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  opacity: 1,
});

const opacityClosedMixin = theme => ({
  transition: theme.transitions.create(['opacity'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  opacity: 0,
});

const DrawerHeader = styled('div', {
  name: 'Sidebar',
  slot: 'header',
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  margin: '20px 0',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const SidebarDrawer = styled(Drawer, {
  shouldForwardProp: prop => prop !== 'open',
  name: 'Sidebar',
  slot: 'root',
})(({ theme }) => ({
  width: SIDEBAR_WIDTH,
  flexShrink: 0,
  display: 'flex',
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
  boxSizing: 'border-box',
  flexDirection: 'column',
  [`& .${accordionSummaryClasses.root}`]: {},
  variants: [
    {
      props: ({ open }) => open,
      style: {
        [`& .${accordionSummaryClasses.root}`]: { ...opacityOpenMixin(theme) },
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        [`& .${accordionSummaryClasses.root}`]: { ...opacityClosedMixin(theme) },
      },
    },
    {
      props: ({ open }) => open,
      style: {
        ...sidebarOpenedMixin(theme),
        [`& .${drawerClasses.paper}`]: sidebarOpenedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...sidebarClosedMixin(theme),
        [`& .${drawerClasses.paper}`]: sidebarClosedMixin(theme),
      },
    },
  ],
}));

const SidebarDivider = styled('hr', {
  name: 'Sidebar',
  slot: 'divider',
})({
  display: 'block',
  height: '1px',
  border: 0,
  borderTop: '1px solid rgb(255 255 255 / .1)',
  padding: 0,
  margin: '6px 12px',
});

const SidebarLogo = styled('img', {
  name: 'Sidebar',
  slot: 'logo',
  shouldForwardProp: prop => prop !== 'sidebarOpen',
})(({ theme }) => ({
  margin: '0 auto',
  maxWidth: '230px',
  maxHeight: '90px',
  objectFit: 'contain',
  variants: [
    {
      props: ({ sidebarOpen }) => sidebarOpen,
      style: { ...opacityOpenMixin(theme) },
    },
    {
      props: ({ sidebarOpen }) => !sidebarOpen,
      style: { ...opacityClosedMixin(theme) },
    },
  ],
}));

const EXPANDED_GROUPS_KEY = 'sidebar-expanded-groups';

const getInitialExpandedGroups = () => {
  try {
    const stored = localStorage.getItem(EXPANDED_GROUPS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const Sidebar = forwardRef((inProps, ref) => {
  const navigate = useNavigate();
  const props = useThemeProps({ props: inProps, name: 'Sidebar' });
  const { sidebarOpen, logoSrc } = props;
  const ownerState = { ...props };

  const { loading } = useSelector(state => state.me);
  const { groups, ungrouped } = useSelector(selectGroupedScreens);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const hasGroups = groups.length > 0;

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const stored = getInitialExpandedGroups();
    if (stored) return stored;
    return groups.reduce((acc, group) => ({ ...acc, [group.id]: true }), { others: true });
  });

  const handleGroupToggle = useCallback(groupId => {
    setExpandedGroups(prev => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      localStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const logOut = () => {
    logoutCleanup();
    navigate(Route.LOGIN);
  };

  return (
    <SidebarDrawer
      ref={ref}
      ownerState={ownerState}
      variant='permanent'
      anchor='left'
      open={sidebarOpen}
      sx={{
        displayPrint: 'none',
      }}
    >
      <div>
        <DrawerHeader>
          <SidebarLogo sidebarOpen={sidebarOpen} src={logoSrc} alt='' />
        </DrawerHeader>
        <div className='flex flex-col px-[10px]'>
          {loading ? (
            <div className='flex justify-center py-4'>
              <CircularProgress sx={{ color: 'white' }} />
            </div>
          ) : hasGroups ? (
            <>
              {groups.map(group => (
                <RouteGroup
                  key={group.id}
                  // Default groups to expanded (the `expandedGroups` initializer runs
                  // before `/me` populates `groups`, so ids are absent on first render);
                  // only an explicit user collapse (false) keeps a group closed.
                  expanded={expandedGroups[group.id] !== false}
                  onChange={() => handleGroupToggle(group.id)}
                  disableGutters
                >
                  <AccordionSummary
                    expandIcon={<SideMenuGroupExpandIcon />}
                    aria-controls={`group-${group.id}-content`}
                    id={`group-${group.id}-header`}
                  >
                    {group.name}
                  </AccordionSummary>
                  <AccordionDetails>
                    <ScreenLinks screens={group.screens} sidebarOpen={sidebarOpen} />
                  </AccordionDetails>
                </RouteGroup>
              ))}
              {ungrouped.length > 0 && (
                <>
                  <SidebarDivider />
                  <div className='flex flex-col gap-[4px] items-center px-[12px]'>
                    <ScreenLinks screens={ungrouped} sidebarOpen={sidebarOpen} />
                  </div>
                </>
              )}
            </>
          ) : (
            <RouteGroup defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<SideMenuGroupExpandIcon />} aria-controls='home-content' id='home-header'>
                Home
              </AccordionSummary>
              <AccordionDetails>
                <ScreenLinks screens={ungrouped} sidebarOpen={sidebarOpen} />
              </AccordionDetails>
            </RouteGroup>
          )}
          <SidebarDivider />
          <div className='flex flex-col gap-[4px] items-center px-[12px]'>
            {/* Static, not screens-grant-driven (flank#4463) — a SUPER_ADMIN-only
                ops tool, same reasoning as the route itself in AppRouter. */}
            {isSuperAdmin && (
              <SidebarLink sidebarOpen={sidebarOpen} to={Route.PARTNER_DATA_HEALTH}>
                <MonitorHeartOutlinedIcon className='mr-[16px]' />
                <span>Partner Data Health</span>
              </SidebarLink>
            )}
            <SidebarLink sidebarOpen={sidebarOpen} to={Route.SETTINGS}>
              <SettingsRouteIcon className='mr-[16px]' />
              <span>Settings</span>
            </SidebarLink>
          </div>
        </div>
      </div>
      <div className='mb-[20px]'>
        <SidebarLink sidebarOpen={sidebarOpen} to={Route.LOGIN} onClick={logOut} sx={{ margin: '0 22px' }}>
          <LogOutIcon className='mr-[16px]' />
          <span>Logout</span>
        </SidebarLink>
      </div>
    </SidebarDrawer>
  );
});

Sidebar.propTypes = {
  sidebarOpen: PropTypes.bool,
  handleSidebarToggle: PropTypes.func,
  logoSrc: PropTypes.string,
};

export default Sidebar;
